import { FC } from 'react';
import { AlertCircle } from 'lucide-react';
import { useGetCoursePlanSuggestionQuery } from '@teachur/http-client';
import type { DegreePlan } from '@teachur/http-client';

import { Button } from '@new/common/ui/Button/Button';

import { BuildPlanLoadingStep } from '../BuildPlanLoadingStep/BuildPlanLoadingStep';

const ERROR_TITLE = 'We couldn’t build your plan';
const ERROR_DESCRIPTION = 'Something went wrong while building your course plan. Please try again.';

interface PlanBuilderProps {
  degreeId: number;
  onPlanBuilt: (plans: DegreePlan[]) => void;
  onCancel: () => void;
}

export const PlanBuilder: FC<PlanBuilderProps> = ({ degreeId, onPlanBuilt, onCancel }) => {
  const suggestion = useGetCoursePlanSuggestionQuery(degreeId, { enabled: true, cacheTime: 0 });

  const planReady =
    suggestion.isSuccess && suggestion.isFetchedAfterMount && !suggestion.isFetching;
  // While failed, the loading step is unmounted, so a retry remounts it and the animation
  // starts over on its own.
  const failed = suggestion.isError && !suggestion.isFetching;

  const onRetry = () => {
    void suggestion.refetch();
  };

  const onLoadingComplete = () => {
    if (suggestion.data) {
      onPlanBuilt(suggestion.data.plans);
    }
  };

  if (failed) {
    return (
      <div
        role="alert"
        className="mx-auto flex w-full max-w-xl flex-col items-center gap-6 px-4 py-10 text-center sm:py-16"
      >
        <span className="flex size-14 items-center justify-center rounded-xl bg-(--primary-O200)">
          <AlertCircle aria-hidden className="size-7 text-(--primary-O1000)" />
        </span>
        <div className="flex flex-col gap-2">
          <h2 className="m-0! [font-family:var(--font-arvo)]! [font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB900)!">
            {ERROR_TITLE}
          </h2>
          <p className="m-0! [font-size:var(--fs-b4)]! text-(--neutral-black-NB600)!">
            {ERROR_DESCRIPTION}
          </p>
        </div>
        <div className="flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row sm:justify-center">
          <Button
            variant="primary"
            className="w-full sm:w-auto"
            text="Try again"
            onClick={onRetry}
          />
          <Button variant="outline" className="w-full sm:w-auto" text="Back" onClick={onCancel} />
        </div>
      </div>
    );
  }

  return <BuildPlanLoadingStep planReady={planReady} onComplete={onLoadingComplete} />;
};
